import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useGetConceptsFiltersMutation } from "../../../store/api/lumi/concepts";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import { ArrowDownOutlined, ArrowUpOutlined, LoadingOutlined } from "@ant-design/icons";
import { Select, Spin, Statistic } from "antd";
import { DateBreakdown, maxDisabledValue, ShowCountOptions } from "../../../constants";
import classes from "./SentimentChangePercent.module.css";
import moment from "moment/moment";
import { debounce } from "lodash";
import { safeDivide } from "../../../utils/safeDivide";
import Scrollbars from "react-custom-scrollbars-2";
import { getComparingDates } from "../../../utils/getCompareDates";

const LargestSentimentMovement = (props) => {
    const {
        selectedDateName,
        interval,
        id,
        filters,
        conceptList,
        setFilterDocCount,
        setLocalConceptOptions,
        seletctedType,
        setIntervalDate,
        selectedDateField,
    } = props;
    const [loading, setLoading] = useState(false);
    const [conceptChanges, setConceptChanges] = useState([]);
    const [conceptChangesCount, setConceptChangesCount] = useState(5);
    const [conceptChangesSort, setConceptChangesSort] = useState("ASC");
    const [disabledIntervals, setDisabledIntervals] = useState({});
    const [comparingDates, setComparingDates] = useState([]);

    const [getVolume] = useGetConceptsFiltersMutation();

    const conceptFilter = useMemo(
        () =>
            JSON.stringify(
                getConceptFilter({ ...props, selectedPageSize: 25 }),
            ),
        [props],
    );

    const getCount = useCallback(
        (concept) => {
            let count = concept.match_count;
            switch (seletctedType) {
                case "exact_match_count":
                    count = concept.exact_match_count;
                    break;
                case "conceptual_match_count":
                    count = concept.match_count - concept.exact_match_count;
                    break;
                default:
                    count = concept.match_count;
                    break;
            }
            return count;
        },
        [seletctedType],
    );

    const getVolumeData = useCallback(async () => {
        setLoading(true);

        const { data: dataConcepts } = await getVolume({
            id,
            concept_selector: JSON.parse(conceptFilter),
            filter: filters,
            breakdowns: [{ name: selectedDateName, interval }],
        });

        setFilterDocCount(dataConcepts.filter_count);

        if (conceptList) {
            setLocalConceptOptions(dataConcepts.match_counts);
        }

        const breakdownData = dataConcepts.breakdowns[0].buckets;

        const lastData = breakdownData[breakdownData.length - 1];
        const preLastData = breakdownData[breakdownData.length - 2];

        const calculatedComparingDates = getComparingDates(preLastData, lastData, interval);
        setComparingDates(calculatedComparingDates);

        const updatedConcepts = lastData.match_counts.map((concept) => {
            const preLastConcept = preLastData.match_counts.find(
                (c) => c.name === concept.name,
            );

            let changePercentage;
            if (preLastConcept) {
                changePercentage = (
                    safeDivide(getCount(concept), lastData.total_count) * 100 -
                    safeDivide(
                        getCount(preLastConcept),
                        preLastData.total_count,
                    ) *
                    100
                ).toFixed(2);
            } else {
                changePercentage =
                    safeDivide(getCount(concept), lastData.total_count) * 100;
            }

            return { ...concept, changePercentage };
        });

        const changes = updatedConcepts.sort(
            (a, b) =>
                Math.abs(b.changePercentage) - Math.abs(a.changePercentage),
        );

        setConceptChanges(changes);

        setLoading(false);
    }, [
        getVolume,
        id,
        conceptFilter,
        filters,
        selectedDateName,
        interval,
        setFilterDocCount,
        conceptList,
        setLocalConceptOptions,
        getCount,
    ]);

    useEffect(() => {
        setLoading(true);
        const debouncedGetSentiments = debounce(async () => {
            await getVolumeData();
        }, 500);

        debouncedGetSentiments();

        return () => {
            debouncedGetSentiments.cancel();
        };
    }, [getVolumeData]);

    useEffect(() => {
        const iterations = calculateIterationsForAllIntervals();
        const firstEnabledInterval =
            Object.keys(iterations).find((key) => !iterations[key].disabled) ||
            "day";
        if (!interval) setInterval(firstEnabledInterval);
        setDisabledIntervals(iterations);
    }, [selectedDateField]);

    const changeSortHandler = () => {
        if (conceptChangesSort === "ASC") {
            setConceptChanges(
                conceptChanges.sort(
                    (a, b) =>
                        Math.abs(a.changePercentage) -
                        Math.abs(b.changePercentage),
                ),
            );
        }
        if (conceptChangesSort === "DESC") {
            setConceptChanges(
                conceptChanges.sort(
                    (a, b) =>
                        Math.abs(b.changePercentage) -
                        Math.abs(a.changePercentage),
                ),
            );
        }
        setConceptChangesSort(conceptChangesSort === "ASC" ? "DESC" : "ASC");
    };

    const calculateIterationsForInterval = (minimum, maximum, interval) => {
        const startDate = moment.utc(minimum);  // Ensure UTC
        const endDate = moment.utc(maximum);    // Ensure UTC
        return endDate.diff(startDate, interval) + 1;
    };

    const calculateIterationsForAllIntervals = () => {
        const { minimum, maximum } = selectedDateField;
        return DateBreakdown.INTERVALS.reduce((acc, interval) => {
            const count = calculateIterationsForInterval(
                minimum,
                maximum,
                interval,
            );
            acc[interval] = { disabled: count > maxDisabledValue, count };
            return acc;
        }, {});
    };
    const height = props.widget.layout.h * 39 - 150;
    useEffect(() => {
        props.setAdditionalFilters([{
            key: "Special",
            label: "Special Filter",
            children:
                <SpecialFilter
                    disabledIntervals={disabledIntervals}
                    conceptChangesCount={conceptChangesCount}
                    interval={interval}
                    ShowCountOptions={ShowCountOptions}
                    setConceptChangesCount={setConceptChangesCount}
                    setIntervalDate={setIntervalDate}
                />,
        }]);
    }, [interval,conceptChangesCount,disabledIntervals]);

    return (
        <div className={classes.container}>
            {loading ? (
                <Spin
                    className={"spinner-by-center"}
                    indicator={
                        <LoadingOutlined style={{ fontSize: 24 }} spin />
                    }
                />
            ) : (
                <div className={classes.change_type_container}>
                    <div className={classes.change_type}>
                        <div
                            onMouseDown={e => e.stopPropagation()}
                            onClick={changeSortHandler}
                            className={classes.change_type_header__sort}
                        >
                            {conceptChangesSort === "ASC" ? (
                                <div>
                                    <ArrowDownOutlined /> DESC
                                </div>
                            ) : (
                                <div>
                                    <ArrowUpOutlined /> ASC
                                </div>
                            )}
                        </div>
                        <Scrollbars
                            style={{
                                height: height,
                            }}
                        >
                            <div
                                className={
                                    classes.change_type_concepts_container
                                }
                            >
                                {conceptChanges
                                    .slice(0, conceptChangesCount)
                                    .map((concept) => (
                                        <div key={concept.name}>
                                            <div
                                                className={classes.concept_name}
                                            >
                                                {concept.name}
                                            </div>
                                            <div
                                                style={{
                                                    textAlign: "end",
                                                    paddingRight:
                                                        conceptChangesCount <=
                                                        10
                                                            ? 0
                                                            : "40px",
                                                }}
                                            >
                                                <Statistic
                                                    value={
                                                        concept.changePercentage
                                                    }
                                                    precision={2}
                                                    valueStyle={{
                                                        color:
                                                            concept.changePercentage >
                                                            0
                                                                ? "#3f8600"
                                                                : "#cf1322",
                                                        fontSize: "16px",
                                                    }}
                                                    formatter={(value) =>
                                                        Math.abs(value).toFixed(
                                                            2,
                                                        )
                                                    }
                                                    prefix={
                                                        concept.changePercentage >
                                                        0 ? (
                                                            <ArrowUpOutlined />
                                                        ) : (
                                                            <ArrowDownOutlined />
                                                        )
                                                    }
                                                    suffix={`% Change ${comparingDates?.length && `${comparingDates?.[1]} -> ${comparingDates?.[0]}`}`}
                                                />
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </Scrollbars>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LargestSentimentMovement;


const SpecialFilter = ({
                           interval,
                           disabledIntervals,
                           conceptChangesCount,
                           setConceptChangesCount,
                           ShowCountOptions,
                           setIntervalDate,
                       }) => {

    const intervalOptions = DateBreakdown.INTERVALS.filter(
        (item) => item !== "hour",
    )
        .map((interval) => ({
            label:
                interval.charAt(0).toUpperCase() +
                (interval === "day" ? "ai" : interval.slice(1)) +
                "ly",
            value: interval,
            disabled: disabledIntervals[interval]?.disabled,
        }))
        .filter((option) => !option.disabled);

    return (
        <div className={classes.select_container} onMouseDown={e => e.stopPropagation()}>
            <div className={classes.select}>
                <label>Select Comparison Period</label>
                <Select
                    showSearch
                    style={{ width: 150 }}
                    placeholder="Select Comparison Period"
                    value={interval}
                    onChange={(value) => {
                        setIntervalDate(value);
                    }}
                    options={intervalOptions}
                    size="small"
                />
            </div>
            <div className={classes.select}>
                <label>Show Concepts</label>
                <Select
                    showSearch
                    style={{ width: 150 }}
                    placeholder="Show Concepts"
                    value={conceptChangesCount}
                    onChange={(value) => {
                        setConceptChangesCount(value);
                    }}
                    options={ShowCountOptions}
                    size="small"
                />
            </div>
        </div>
    );

};
